import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { RepeatIcon } from '../ui/icons';

function formatDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function stateLabel(state) {
  if (!state) return 'Unknown';
  return state
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * v1.17.0 "Rollover status card" — mirrors PayoutInterface's shape/spirit:
 * every figure here is read directly off the investment's `rollover` block
 * (single-record GET only, never present on list responses — this card
 * must never be rendered from a list-fetched investment).
 *
 * Three distinct states, never conflated:
 *  1. `scheduledRollover` present -> a rollover is queued to auto-execute
 *     on the final interest payout or the nightly sweep. Shown regardless
 *     of `canRollover`, since a scheduled rollover and "can I start a new
 *     one" are different questions.
 *  2. `canRollover: true`, nothing scheduled -> eligible now, show the
 *     action button.
 *  3. `canRollover: false` -> show `state`/`reason` as explanatory text.
 *     Never hidden silently — an admin needs to know *why* rollover isn't
 *     available (window not open yet, term already has payments, max
 *     rollovers reached, etc.), and the backend's `reason` string is
 *     written to explain exactly that.
 *
 * `onRollover`/`onCancelRollover` are only passed on the admin side —
 * omitting them renders the investor's read-only view, same pattern as
 * PayoutInterface's `onRecordPayout`.
 */
export default function RolloverStatusCard({ rollover, onRollover, onCancelRollover }) {
  if (!rollover) return null;

  const hasScheduled = !!rollover.scheduledRollover;
  const showUsageBadge = rollover.maxRollovers > 0;

  return (
    <Card className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <RepeatIcon className="text-foreground-muted" />
          <h2 className="text-sm font-semibold text-foreground">Rollover</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showUsageBadge && (
            <Badge tone="neutral">
              {rollover.timesRolledOver} of {rollover.maxRollovers} rollovers used
            </Badge>
          )}
          {hasScheduled ? (
            <Badge tone="warning">Rollover scheduled</Badge>
          ) : (
            <Badge tone={rollover.canRollover ? 'success' : 'neutral'}>{stateLabel(rollover.state)}</Badge>
          )}
        </div>
      </div>

      {hasScheduled ? (
        <div className="rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 space-y-1">
          <p className="text-sm font-medium text-warning">
            Rollover scheduled — will execute once the final interest for this term is paid.
          </p>
          <p className="text-xs text-warning/80">
            Requested {formatDateOnly(rollover.scheduledRollover.scheduledAt)}
            {rollover.scheduledRollover.scheduledBy?.name ? ` by ${rollover.scheduledRollover.scheduledBy.name}` : ''}
          </p>
        </div>
      ) : rollover.canRollover ? (
        <p className="text-sm text-foreground-muted">
          This investment is eligible for rollover — the current term can be extended instead of closed.
        </p>
      ) : (
        <div className="rounded-xl border border-border bg-background-elevated px-4 py-3">
          <p className="text-sm text-foreground-muted">
            {rollover.reason || 'This investment is not currently eligible for rollover.'}
          </p>
          {rollover.windowOpensAt && (
            <p className="text-xs text-foreground-subtle mt-1">Window opens {formatDateOnly(rollover.windowOpensAt)}</p>
          )}
        </div>
      )}

      {(onRollover || onCancelRollover) && (
        <div className="pt-1 flex justify-end gap-2">
          {hasScheduled && onCancelRollover && (
            <Button size="sm" variant="secondary" onClick={onCancelRollover}>
              Cancel scheduled rollover
            </Button>
          )}
          {!hasScheduled && rollover.canRollover && onRollover && (
            <Button size="sm" variant="secondary" onClick={onRollover}>
              Roll over
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
